import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import { getDefaultPaginationState } from '@/stores/shared/pagination'
import { useMessagesStore } from '@/stores/messages'

export const useIssuesStore = defineStore('issues', {
  state: () => ({
    ...getDefaultPaginationState(),
    issues: [],
    fetchInitiated: false,
    allFetched: false
  }),

  getters: {
    forPublication: (state) => (publication) => {
      const publicationId = publication?.id
      if (!publicationId) return []

      return state.issues.filter(issue => issue?.publication?.id == publicationId)
    },

    forId: (state) => (id) => {
      return state.issues.find(issue => issue.id === id)
    },

    allAsObject: (state) => {
      const obj = {}
      state.issues.forEach(issue => {
        const label = `${issue.publication.name}: ${issue.title} (${issue.publish_on})`
        obj[label] = issue.id
      })
      return obj
    },

    allAsArray: (state) => {
      return state.issues.map(issue => ({
        label: `${issue.publication.name}: ${issue.title} (${issue.publish_on})`,
        value: issue.id
      }))
    }
  },

  actions: {
    setPageCurrent(page) {
      this.page.current = page
    },

    setCurrent({ id, current }) {
      const issue = this.issues.find(iss => iss.id === id)

      // If we're setting a new current issue for the publication,
      // we need to set the old current publication issue to no longer be current.
      if (current) {
        const oldCurrent = this.issues.find(iss => iss.publication.id === issue.publication.id && iss.current)
        if (oldCurrent) oldCurrent.current = false
      }

      issue.current = current
    },

    remove(issueId) {
      const i = this.issues.map(issue => issue.id).indexOf(issueId)
      this.issues.splice(i, 1)
    },

    async fetch(options = {}) {
      this.fetchInitiated = true
      if (options?.page) this.setPageCurrent(options.page)
      const page = this.page.current

      return httpClient
        .get('/api/internal/issues', { params: { page } })
        .then(({ data: { meta, issues } }) => {
          this.page = meta.page
          this.issues = issues
        })
        .catch(err => {
          console.error(err)
          useMessagesStore().addError(err)
        })
    },

    async fetchOnce() {
      if (this.fetchInitiated) return Promise.resolve()
      return this.fetch()
    },

    async fetchAll() {
      this.fetchInitiated = true
      return httpClient
        .get('/api/internal/issues', { params: { per_page: 500 } })
        .then(({ data: { issues } }) => {
          this.issues = issues
          this.allFetched = true
        })
        .catch(err => {
          console.error(err)
          useMessagesStore().addError(err)
        })
    },

    async fetchAllOnce() {
      if (this.allFetched) return Promise.resolve()
      return this.fetchAll()
    },

    async toggleCurrent({ id, current }) {
      return httpClient
        .patch(
          `/api/internal/issues/${id}`,
          { current }
        )
        .then(() => {
          this.setCurrent({ id, current })
          useMessagesStore().addNotice('Current issue set')
        })
        .catch(err => {
          useMessagesStore().addError(err)
          throw err
        })
    },

    async destroy({ id }) {
      return httpClient.delete(`/api/internal/issues/${id}`).then(() => {
        useMessagesStore().addNotice('Issue deleted')
        this.remove(id)
      }).catch(err => {
        console.error(err)
        useMessagesStore().addError(err)
      })
    }
  }
})
